// Salvaguarda bloqueante de descuadre entre el total esperado del carrito y
// el total que realmente arma `crearPreferencia` de Mercado Pago (el backend
// recalcula precios desde BD, ver Sima-apirest/crearPreferencia.post.ts —
// nunca confía en lo que la web envía). Módulo puro, sin React/Zustand/
// Sentry, mismo criterio que src/lib/mpReturn.js: la tabla de reglas debe
// poder testearse aislada y MercadoPagoButton.jsx debe ejercitar la función
// real, nunca un mock.

// Tolerancia de ±$1: el backend redondea el precio por línea y luego reparte
// el cupón (Sima-apirest/crearPreferencia.post.ts), mientras que la web
// redondea el total agregado — un cupón porcentual sobre precios impares
// puede producir una diferencia de $1 legítima. Con tolerancia $0 estricta
// esa diferencia bloquearía un pago válido. Palanca de emergencia: subir esta
// constante a Infinity desactiva el bloqueo sin tocar más código.
export const MP_TOTAL_TOLERANCIA = 1;

export const MP_MSG_DESCUADRE =
  "No se pudo confirmar el monto del pago. Por favor intenta nuevamente.";

// Clave de sessionStorage nueva y aditiva: no reemplaza ni renombra
// mp_buyOrder/mp_storeId.
export const MP_SNAPSHOT_KEY = "mp_checkout_snapshot";
// 24h, paridad con checkoutSnapshot.js de Sima-appmovil.
export const MP_SNAPSHOT_TTL_MS = 24 * 60 * 60 * 1000;

const NUMERIC_STRING_RE = /^-?\d+(\.\d+)?$/;

// Number(null) === 0, Number([]) === 0, Number(" ") === 0 — no basta con
// Number.isFinite(Number(x)): hay que rechazar explícitamente cualquier
// valor que no sea un number o un string que matchee un patrón numérico.
const parseVerifiableNumber = (value) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!NUMERIC_STRING_RE.test(trimmed)) return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null; // undefined, null, boolean, objeto, array -> no verificable
};

/**
 * verificarTotalPreferencia({ totalPreferencia, totalEsperado })
 * Devuelve { estado: "ok" | "no_verificable" | "descuadre", totalPreferencia: number|null, diferencia: number|null }.
 * "no_verificable" nunca bloquea (compatibilidad hacia atrás, CA-4/CA-7).
 */
export const verificarTotalPreferencia = ({ totalPreferencia, totalEsperado }) => {
  const parsed = parseVerifiableNumber(totalPreferencia);

  if (parsed === null) {
    return { estado: "no_verificable", totalPreferencia: null, diferencia: null };
  }

  const diferencia = parsed - totalEsperado;
  if (Math.abs(diferencia) <= MP_TOTAL_TOLERANCIA) {
    return { estado: "ok", totalPreferencia: parsed, diferencia };
  }

  return { estado: "descuadre", totalPreferencia: parsed, diferencia };
};

// buy_order es varchar(20) en BD y se reutiliza como external_reference en
// Mercado Pago. Centralizado aquí para poder regenerarlo en un reintento tras
// un bloqueo sin duplicar el literal (antes vivía sólo en CartPage.jsx).
export const generarBuyOrderMP = () =>
  "MP-" + Date.now().toString(36) + "-" + Math.round(Math.random() * 9999);

// Helpers de snapshot (Bloque C): usados sólo como referencia de
// verificación post-retorno, nunca para calcular el `total` de createOrder.
// Todos envueltos en try/catch — nunca deben lanzar ni hacer fallar el pago
// (Safari privado histórico, QuotaExceededError, JSON corrupto, etc.).

export const guardarSnapshotMP = ({ buyOrder, storeId, total, subtotal, descuento }) => {
  try {
    sessionStorage.setItem(
      MP_SNAPSHOT_KEY,
      JSON.stringify({
        buyOrder,
        storeId,
        total,
        subtotal,
        descuento,
        createdAt: Date.now(),
      }),
    );
  } catch {
    // sessionStorage no disponible/lleno: el snapshot es solo-verificación,
    // no debe impedir que el pago continúe.
  }
};

export const leerSnapshotMP = (buyOrder) => {
  try {
    const raw = sessionStorage.getItem(MP_SNAPSHOT_KEY);
    if (!raw) return null;

    const snapshot = JSON.parse(raw);
    if (!snapshot || snapshot.buyOrder !== buyOrder) return null; // snapshot rancio de otro buyOrder
    if (Date.now() - snapshot.createdAt > MP_SNAPSHOT_TTL_MS) return null;

    return snapshot;
  } catch {
    return null;
  }
};

export const limpiarSnapshotMP = () => {
  try {
    sessionStorage.removeItem(MP_SNAPSHOT_KEY);
  } catch {
    // no-op
  }
};
